// src/components/admin/reports/QueryBuilder.jsx
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { getMembers, getVolunteers, getShifts } from '../../../api/communityApi';
import { getEvents } from '../../../api/eventsApi';
import { getAllCampaigns } from '../../../api/communicationsApi';

// ── Field definitions per data source ──────────────────────────────────────
const SOURCE_FIELDS = {
  members: ['displayName', 'email', 'district', 'widowStage', 'employmentStatus', 'engagementTier', 'engagementScore', 'createdAt'],
  volunteers: ['displayName', 'email', 'status', 'district', 'totalHours', 'skills', 'createdAt'],
  events: ['title', 'category', 'status', 'startDate', 'capacity', 'registrationCount'],
  eventRegistrations: ['memberId', 'memberName', 'status', 'registeredAt', 'checkedIn'],
  shifts: ['title', 'category', 'status', 'date', 'maxSlots', 'signedUpCount'],
  shiftAssignments: ['volunteerId', 'volunteerName', 'shiftTitle', 'shiftDate', 'status', 'hoursLogged'],
  emailCampaigns: ['name', 'status', 'audienceType', 'recipientCount', 'sentAt'],
  pushCampaigns: ['name', 'status', 'audienceType', 'recipientCount', 'sentAt'],
  whatsappCampaigns: ['name', 'status', 'audienceType', 'recipientCount', 'sentAt'],
};

const SOURCE_LABELS = {
  members: 'Members',
  volunteers: 'Volunteers',
  events: 'Events',
  eventRegistrations: 'Event Registrations',
  shifts: 'Shifts',
  shiftAssignments: 'Shift Assignments',
  emailCampaigns: 'Email Campaigns',
  pushCampaigns: 'Push Campaigns',
  whatsappCampaigns: 'WhatsApp Campaigns',
};

const OPERATORS = [
  { value: 'equals',       label: 'equals' },
  { value: 'not_equals',   label: 'not equals' },
  { value: 'contains',     label: 'contains' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than',    label: 'less than' },
  { value: 'before',       label: 'before' },
  { value: 'after',        label: 'after' },
  { value: 'in',           label: 'in (comma-separated)' },
];

const applyFilters = (data, filters) => {
  if (!filters || filters.length === 0) return data;
  return data.filter(row => {
    return filters.every(f => {
      if (!f.field || !f.operator) return true;
      const val = row[f.field];
      const fv  = f.value;
      switch (f.operator) {
        case 'equals':       return String(val) === String(fv);
        case 'not_equals':   return String(val) !== String(fv);
        case 'contains':
          if (Array.isArray(val)) return val.includes(fv);
          return String(val || '').toLowerCase().includes(String(fv).toLowerCase());
        case 'greater_than': return Number(val) > Number(fv);
        case 'less_than':    return Number(val) < Number(fv);
        case 'before':       return val && String(val) < String(fv);
        case 'after':        return val && String(val) > String(fv);
        case 'in': {
          const list = String(fv).split(',').map(s => s.trim());
          return list.includes(String(val));
        }
        default: return true;
      }
    });
  });
};

const downloadCSV = (data, filename = 'query-results.csv') => {
  if (!data || data.length === 0) return;
  const keys = Object.keys(data[0]).filter(k => k !== 'id');
  const header = keys.join(',');
  const rows = data.map(row =>
    keys.map(k => `"${String(row[k] ?? '').replace(/"/g, '""')}"`).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const fmtTs = (ts) => {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-SG');
  } catch { return String(ts); }
};

export default function QueryBuilder({ orgId, adminId }) {
  const [source, setSource]       = useState('members');
  const [filters, setFilters]     = useState([]);
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [queryName, setQueryName] = useState('');
  const [saving, setSaving]       = useState(false);
  const [savedQueries, setSavedQueries]   = useState([]);
  const [savedLoading, setSavedLoading]   = useState(false);
  const [hasRun, setHasRun]       = useState(false);

  const fields = SOURCE_FIELDS[source] || [];

  // Load saved queries
  useEffect(() => {
    loadSavedQueries();
  }, [orgId]);

  const loadSavedQueries = async () => {
    setSavedLoading(true);
    try {
      const q = query(
        collection(db, 'organizations', orgId, 'savedQueries'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setSavedQueries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading saved queries:', e);
    } finally {
      setSavedLoading(false);
    }
  };

  const fetchData = async () => {
    if (!orgId) return [];
    switch (source) {
      case 'members':          return await getMembers(orgId);
      case 'volunteers':       return await getVolunteers(orgId);
      case 'events':           return await getEvents(orgId);
      case 'shifts':           return await getShifts(orgId);
      case 'emailCampaigns':
      case 'pushCampaigns':
      case 'whatsappCampaigns': {
        const all = await getAllCampaigns(orgId);
        const channel = source === 'emailCampaigns' ? 'email' : source === 'pushCampaigns' ? 'push' : 'whatsapp';
        return all.filter(c => c._channel === channel);
      }
      default: {
        const snap = await getDocs(
          collection(db, 'organizations', orgId, source)
        );
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
    }
  };

  const handleRunQuery = async () => {
    setLoading(true);
    setError('');
    setResults([]);
    setHasRun(false);
    try {
      const raw  = await fetchData();
      // Normalise timestamps in display fields
      const normalised = raw.map(row => {
        const out = { ...row };
        ['createdAt', 'updatedAt', 'sentAt', 'date', 'start_datetime', 'startDate'].forEach(k => {
          if (out[k] && typeof out[k] === 'object' && out[k].toDate) {
            out[k] = fmtTs(out[k]);
          }
        });
        return out;
      });
      const filtered = applyFilters(normalised, filters);
      setResults(filtered);
      setHasRun(true);
    } catch (e) {
      setError('Failed to fetch data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFilter = () => {
    setFilters(prev => [...prev, { field: fields[0] || '', operator: 'equals', value: '' }]);
  };

  const handleFilterChange = (idx, key, val) => {
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  };

  const handleRemoveFilter = (idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveQuery = async () => {
    if (!queryName.trim()) return alert('Please enter a name for this query.');
    setSaving(true);
    try {
      await addDoc(collection(db, 'organizations', orgId, 'savedQueries'), {
        name:    queryName.trim(),
        source,
        filters,
        createdBy: adminId,
        createdAt: serverTimestamp(),
      });
      setQueryName('');
      await loadSavedQueries();
    } catch (e) {
      alert('Failed to save query: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRunSaved = async (sq) => {
    setSource(sq.source || 'members');
    setFilters(sq.filters || []);
    // Run after state settles
    setTimeout(handleRunQuery, 50);
  };

  const handleDeleteSaved = async (id) => {
    if (!window.confirm('Delete this saved query?')) return;
    try {
      await deleteDoc(doc(db, 'organizations', orgId, 'savedQueries', id));
      setSavedQueries(prev => prev.filter(q => q.id !== id));
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  const visibleColumns = results.length > 0
    ? Object.keys(results[0]).filter(k => k !== 'id' && k !== 'orgId' && !k.startsWith('_')).slice(0, 10)
    : [];

  return (
    <div className="bb-panel">
      <div className="bb-panel-header">
        <h3 className="bb-panel-title">Query Builder</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
          Build custom data queries and export results.
        </p>
      </div>
      <div className="bb-panel-body">

        {/* Saved Queries */}
        {savedQueries.length > 0 && (
          <div className="bb-qb-saved" style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
              Saved Queries
            </h4>
            {savedLoading ? (
              <p style={{ fontSize: 13, color: '#9CA3AF' }}>Loading...</p>
            ) : (
              savedQueries.map(sq => (
                <div key={sq.id} className="bb-qb-saved-item">
                  <div>
                    <div className="bb-qb-saved-name">{sq.name}</div>
                    <div className="bb-qb-saved-meta">
                      Source: {SOURCE_LABELS[sq.source] || sq.source} &bull; {sq.filters?.length || 0} filter(s)
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="bb-btn bb-btn-sm bb-btn-secondary" onClick={() => handleRunSaved(sq)}>
                      Run
                    </button>
                    <button
                      className="bb-btn bb-btn-sm"
                      style={{ background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA' }}
                      onClick={() => handleDeleteSaved(sq.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Data Source */}
        <div style={{ marginBottom: 20 }}>
          <label className="bb-label">Data Source</label>
          <select
            className="bb-input"
            style={{ maxWidth: 320 }}
            value={source}
            onChange={e => { setSource(e.target.value); setFilters([]); setResults([]); setHasRun(false); }}
          >
            {Object.entries(SOURCE_LABELS).map(([val, lbl]) => (
              <option key={val} value={val}>{lbl}</option>
            ))}
          </select>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label className="bb-label" style={{ marginBottom: 0 }}>Filters</label>
            <button className="bb-btn bb-btn-sm bb-btn-secondary" onClick={handleAddFilter}>
              + Add Filter
            </button>
          </div>

          {filters.length === 0 && (
            <p style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
              No filters — query will return all records.
            </p>
          )}

          {filters.map((f, idx) => (
            <div key={idx} className="bb-qb-filter-row">
              <select
                value={f.field}
                onChange={e => handleFilterChange(idx, 'field', e.target.value)}
              >
                {(SOURCE_FIELDS[source] || []).map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>

              <select
                value={f.operator}
                onChange={e => handleFilterChange(idx, 'operator', e.target.value)}
              >
                {OPERATORS.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>

              <input
                type="text"
                placeholder="value"
                value={f.value}
                onChange={e => handleFilterChange(idx, 'value', e.target.value)}
                style={{ flex: 1, minWidth: 120 }}
              />

              <button className="bb-qb-remove" onClick={() => handleRemoveFilter(idx)} title="Remove filter">
                &times;
              </button>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
          <button
            className="bb-btn bb-btn-primary"
            onClick={handleRunQuery}
            disabled={loading}
          >
            {loading ? 'Running...' : 'Run Query'}
          </button>

          {hasRun && results.length > 0 && (
            <button
              className="bb-btn bb-btn-secondary"
              onClick={() => downloadCSV(results, `${source}-query.csv`)}
            >
              Export CSV
            </button>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
            <input
              type="text"
              className="bb-input"
              placeholder="Query name..."
              value={queryName}
              onChange={e => setQueryName(e.target.value)}
              style={{ width: 200 }}
            />
            <button
              className="bb-btn bb-btn-secondary"
              onClick={handleSaveQuery}
              disabled={saving || !queryName.trim()}
            >
              {saving ? 'Saving...' : 'Save Query'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bb-alert bb-alert-error" style={{ marginBottom: 16 }}>{error}</div>
        )}

        {/* Results */}
        {hasRun && (
          <div className="bb-qb-results">
            <div className="bb-qb-results-header">
              <h4>{results.length} record{results.length !== 1 ? 's' : ''} returned</h4>
              <span style={{ fontSize: 12, color: '#6B7280' }}>
                Source: {SOURCE_LABELS[source]}
              </span>
            </div>

            {results.length === 0 ? (
              <p style={{ padding: 20, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
                No records match the applied filters.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table">
                  <thead>
                    <tr>
                      {visibleColumns.map(col => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.slice(0, 200).map((row, i) => (
                      <tr key={row.id || i}>
                        {visibleColumns.map(col => (
                          <td key={col} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {Array.isArray(row[col])
                              ? row[col].join(', ')
                              : row[col] ?? '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {results.length > 200 && (
                  <p style={{ padding: '8px 16px', fontSize: 12, color: '#9CA3AF' }}>
                    Showing first 200 of {results.length} records. Export CSV for full results.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
